import { useEffect, useState, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  flexRender,
} from "@tanstack/react-table";
import { apiClient } from "../services/api";
import PageLayout from "../components/PageLayout";
import TableInput from "../components/TableInput";
import Dropdown from "../components/Dropdown";
import { GRADE_FILTER_OPTIONS } from "../components/Grade";
import { renderClassBadges } from "../utils/classBadgeUtils";
import {
  tanstackTableStyle,
  getTanstackThStyle,
  sortHeaderStyle,
  sortArrowStyle,
  filterContainerStyle,
  tanstackTrStyle,
  getTanstackTdStyle,
  emptyRowStyle,
} from "../components/Style";

type StudentsHistoryRow = {
  id?: string;
  studentNumber: string;
  name: string;
  grade: string;
  class: string;
  actionType: string;
  changes?: string;
  performedBy?: string;
  createdAt?: string;
};

type DateRangeFilter = "all" | "1week" | "1month" | "3months";

const columnHelper = createColumnHelper<StudentsHistoryRow>();

function StudentsHistoryPage() {
  const [rows, setRows] = useState<StudentsHistoryRow[]>([]);
  const [dateRange, setDateRange] = useState<DateRangeFilter>("all");
  const [classes, setClasses] = useState<any[]>([]);
  const [actionTypeFilter, setActionTypeFilter] = useState("");
  
  // TanStack Table states
  const [sorting, setSorting] = useState<SortingState>([
    { id: "createdAt", desc: true }
  ]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const fetchStudentsHistory = async () => {
    try {
      const data = await apiClient.getStudentsHistory({});
      setRows(data);
    } catch (error) {
      console.error("학생 이력 조회 실패:", error);
    }
  };

  const getDateRangeStart = () => {
    const now = new Date();
    const start = new Date();
    switch (dateRange) {
      case "1week":
        start.setDate(now.getDate() - 7);
        break;
      case "1month":
        start.setMonth(now.getMonth() - 1);
        break;
      case "3months":
        start.setMonth(now.getMonth() - 3);
        break;
      default:
        return null;
    }
    return start;
  };

  const isWithinDateRange = (dateStr?: string) => {
    if (!dateStr || dateRange === "all") return true;
    const date = new Date(dateStr);
    const startDate = getDateRangeStart();
    if (!startDate) return true;
    return date >= startDate;
  };

  const fetchClasses = async () => {
    try {
      const data = await apiClient.getClasses();
      setClasses(data);
    } catch (error) {
      console.error("반 목록 조회 실패:", error);
    }
  };

  useEffect(() => {
    fetchStudentsHistory();
    fetchClasses();
    // eslint-disable-next-line
  }, []);

  // 반 목록 추출
  const classList = useMemo(() => {
    return classes.map(c => c.className).sort();
  }, [classes]);

  // 날짜 범위 필터링된 데이터
  const filteredByDateRange = useMemo(() => {
    return rows.filter((r) => isWithinDateRange(r.createdAt));
  }, [rows, dateRange]);

  // 액션 타입 필터링
  const filteredRows = useMemo(() => {
    if (!actionTypeFilter) return filteredByDateRange;
    return filteredByDateRange.filter((r) => r.actionType === actionTypeFilter);
  }, [filteredByDateRange, actionTypeFilter]);

  // 컬럼 정의
  const columns = useMemo(() => [
    columnHelper.accessor("studentNumber", {
      header: "출결번호",
      size: 70,
      cell: (info) => info.getValue(),
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("name", {
      header: "이름",
      size: 70,
      cell: (info) => info.getValue(),
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("grade", {
      header: "학년",
      size: 70,
      cell: (info) => info.getValue(),
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("class", {
      header: "반",
      size: 140,
      cell: (info) => info.getValue(),
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("actionType", {
      header: "액션",
      size: 50,
      cell: (info) => {
        const labels: {[key: string]: string} = { 
          CREATE: "추가", 
          UPDATE: "수정", 
          DELETE: "삭제" 
        };
        return labels[info.getValue()] || info.getValue();
      },
      sortingFn: "alphanumeric",
      enableColumnFilter: false,
    }),
    columnHelper.accessor("changes", {
      header: "변경 내용",
      size: 240,
      cell: (info) => info.getValue() || "-",
      enableSorting: false,
      enableColumnFilter: false,
    }),
    columnHelper.accessor("performedBy", {
      header: "실행자",
      size: 70,
      cell: (info) => info.getValue() || "-",
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("createdAt", {
      header: "일시",
      size: 140,
      cell: (info) => {
        const value = info.getValue();
        return value ? new Date(value).toLocaleString("ko-KR", { hour12: false }) : "";
      },
      sortingFn: "datetime",
      enableColumnFilter: false,
    }),
  ], []);

  // 테이블 인스턴스
  const table = useReactTable({
    data: filteredRows,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 30,
      },
    },
  });

  return (
    <PageLayout title="학생 관리 이력">
      <style>
        {`
          .students-history-table tbody tr:hover {
            background-color: #f6f8fa;
          }
        `}
      </style>
      <div style={{ overflow: "auto", maxHeight: "calc(100vh - 180px)" }}>
        <table className="students-history-table" style={tanstackTableStyle}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;
                  
                  return (
                    <th
                      key={header.id}
                      style={getTanstackThStyle(false, header.getSize(), {})}
                    >
                      {header.isPlaceholder ? null : (
                        <div>
                          {/* Header with sort */}
                          {header.column.getCanSort() ? (
                            <div
                              onClick={header.column.getToggleSortingHandler()}
                              style={sortHeaderStyle}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: <span style={sortArrowStyle}>▲</span>,
                                desc: <span style={sortArrowStyle}>▼</span>,
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          ) : (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )
                          )}
                          
                          {/* Filter row */}
                          <div style={filterContainerStyle}>
                            {columnId === "studentNumber" && (
                              <TableInput
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(e) => header.column.setFilterValue(e.target.value)}
                                placeholder="검색"
                              />
                            )}
                            {columnId === "name" && (
                              <TableInput
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(e) => header.column.setFilterValue(e.target.value)}
                                placeholder="검색"
                              />
                            )}
                            {columnId === "grade" && (
                              <Dropdown
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(value) => header.column.setFilterValue(value || undefined)}
                                options={[
                                  { value: "", label: "전체" },
                                  ...GRADE_FILTER_OPTIONS,
                                ]}
                              />
                            )}
                            {columnId === "class" && (
                              <Dropdown
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(value) => header.column.setFilterValue(value || undefined)}
                                options={[
                                  { value: "", label: "전체" },
                                  ...classList.map((c) => ({ value: c, label: c })),
                                ]}
                              />
                            )}
                            {columnId === "actionType" && (
                              <Dropdown
                                value={actionTypeFilter}
                                onChange={(value) => setActionTypeFilter(value)}
                                options={[
                                  { value: "", label: "전체" },
                                  { value: "CREATE", label: "추가" },
                                  { value: "UPDATE", label: "수정" },
                                  { value: "DELETE", label: "삭제" },
                                ]}
                              />
                            )}
                            {columnId === "performedBy" && (
                              <TableInput
                                value={(header.column.getFilterValue() as string) ?? ""}
                                onChange={(e) => header.column.setFilterValue(e.target.value)}
                                placeholder="검색"
                              />
                            )}
                            {columnId === "createdAt" && (
                              <Dropdown
                                value={dateRange}
                                onChange={(value) => setDateRange(value as DateRangeFilter)}
                                options={[
                                  { value: "all", label: "전체" },
                                  { value: "1week", label: "1주" },
                                  { value: "1month", label: "1개월" },
                                  { value: "3months", label: "3개월" },
                                ]}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={emptyRowStyle}
                >
                  학생 관리 이력이 없습니다.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} style={tanstackTrStyle}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={getTanstackTdStyle(false, cell.column.getSize())}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      <div style={{ margin: "32px 0", display: "flex", justifyContent: "center", alignItems: "center", gap: "22px" }}>
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          style={{
            borderRadius: "10px",
            border: "1px solid #90b8ff",
            background: "#f2f6ff",
            color: "#2466c2",
            padding: "6px 18px",
            fontWeight: "bold",
            fontSize: "17px",
            cursor: table.getCanPreviousPage() ? "pointer" : "not-allowed",
            minWidth: "70px",
            opacity: table.getCanPreviousPage() ? 1 : 0.5,
          }}
        >
          이전
        </button>
        <span style={{ fontWeight: "bold", fontSize: "18px" }}>
          페이지 {table.getState().pagination.pageIndex + 1} / {table.getPageCount() || 1}
        </span>
        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          style={{
            borderRadius: "10px",
            border: "1px solid #90b8ff",
            background: "#f2f6ff",
            color: "#2466c2",
            padding: "6px 18px",
            fontWeight: "bold",
            fontSize: "17px",
            cursor: table.getCanNextPage() ? "pointer" : "not-allowed",
            minWidth: "70px",
            opacity: table.getCanNextPage() ? 1 : 0.5,
          }}
        >
          다음
        </button>
      </div>
    </PageLayout>
  );
}

export default StudentsHistoryPage;
